import { displayText } from '../../../terminology/store';
import { useTerminology } from '../../../terminology/TerminologyContext';
import React, { useState, useRef, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import ThemeContext from '../../ThemeContext';
import Pagination from '../../Utils/Pagination';
import SuperadminConfig from '../../../config/Superadmin';

const Resellers = ({ users, setUsers }) => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const navigate  = useNavigate();
  const location  = useLocation();

  // ── Config ─
  const cfg    = SuperadminConfig.resellers;   // singular, plural, emptyMessage
  const routes = SuperadminConfig.routes;      // base, resellers, addReseller, editReseller, nested.*

  const isSuperadmin = location.pathname.startsWith(routes.base);
  const basePath     = isSuperadmin ? routes.base : '/admin';

  //
  // If you rename a segment in SuperadminConfig these three update automatically.
  const listPath            = `${basePath}/${routes.resellers}`;
  const addResellerPath     = `${basePath}/${routes.addReseller}`;
  const editResellerPath    = (id) => `${basePath}/${routes.editReseller}/${id}`;
  const hierarchyPath       = `${basePath}/${routes.nested.resellerHierarchy}`;

  // ── Theme ──
  const fontFamily    = theme.typography?.fontFamily || '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const titleFontSize = theme.typography?.titleFontSize || 24;
  const bodyFontSize  = theme.typography?.bodyFontSize  || 14;

  // ── State ──
  const [searchTerm,        setSearchTerm]        = useState('');
  const [highlightedUserId, setHighlightedUserId] = useState(null);
  const [highlightedField,  setHighlightedField]  = useState(null);
  const [hoveredUserId,     setHoveredUserId]     = useState(null);
  const searchInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage,     setPerPage]     = useState(10);
  const [gotoPage,    setGotoPage]    = useState('');
  const [showModal,      setShowModal]      = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [actionType,     setActionType]     = useState('');
  const [sortField, setSortField] = useState('reseller_name');
  const [sortOrder, setSortOrder] = useState('asc');

  // ── Handlers ─────────────
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setHighlightedUserId(null);
    setHighlightedField(null);
    setCurrentPage(1);
  };

  const handleSearchSubmit = () => {
    const term        = searchTerm.toLowerCase();
    const filteredAll = (users || []).filter((u) => u.role === 'reseller');
    const match       = filteredAll.find((u) =>
      u.id.toString() === searchTerm ||
      u.reseller_name.toLowerCase() === term ||
      (u.type  || 'none').toLowerCase() === term ||
      (u.level || 'none').toLowerCase() === term ||
      u.primary_contact.toLowerCase() === term
    );
    if (match) {
      setHighlightedUserId(match.id);
      if      (match.id.toString() === searchTerm)             setHighlightedField('id');
      else if (match.reseller_name.toLowerCase() === term)     setHighlightedField('reseller_name');
      else if ((match.type  || 'none').toLowerCase() === term) setHighlightedField('type');
      else if ((match.level || 'none').toLowerCase() === term) setHighlightedField('level');
      else if (match.primary_contact.toLowerCase() === term)   setHighlightedField('primary_contact');
    } else {
      setHighlightedUserId(null);
      setHighlightedField(null);
    }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter') handleSearchSubmit(); };

  const handleActivateDeactivate = (userId, currentStatus) => {
    setSelectedUserId(userId);
    setActionType(currentStatus === 'Active' ? 'deactivate' : 'activate');
    setShowModal(true);
  };

  const confirmAction = () => {
    const userId        = selectedUserId;
    const currentStatus = (users || []).find((u) => u.id === userId)?.status;
    const newStatus     = currentStatus === 'Active' ? 'Deactivated' : 'Active';

    setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, status: newStatus } : u));

    const token = localStorage.getItem('token');
    axios
      .put(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/webmanager/resellers/${userId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        toast.success(`${cfg.singular} status updated successfully!`);
        setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, status: res.data.reseller.status } : u));
      })
      .catch(() => {
        setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, status: currentStatus } : u));
        toast.error(`Failed to update ${cfg.singular.toLowerCase()} status.`);
      });

    setShowModal(false);
    setSelectedUserId(null);
    setActionType('');
  };

  const handleSort = (field) => {
    setSortField(field);
    setSortOrder(sortField === field && sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const getSortValue = (user, field) => {
    if (field === 'id')             return user.id;
    if (field === 'reseller_since') return new Date(user.reseller_since);
    const map = {
      type:       user.type  || 'None',
      level:      user.level || 'None',
      status:     user.status,
      video_conf: user.video_conf === 'enable' ? 'Enabled' : 'Disabled',
    };
    const val = map[field] ?? user[field];
    return typeof val === 'string' ? val.toLowerCase() : val;
  };

  const sortedUsers = (users || [])
    .filter((u) =>
      u.role === 'reseller' &&
      (u.id.toString().includes(searchTerm) ||
       u.reseller_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
       (u.type  || 'none').toLowerCase().includes(searchTerm.toLowerCase()) ||
       (u.level || 'none').toLowerCase().includes(searchTerm.toLowerCase()) ||
       u.primary_contact.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      const vA  = getSortValue(a, sortField);
      const vB  = getSortValue(b, sortField);
      const cmp = vA instanceof Date && vB instanceof Date
        ? vA.getTime() - vB.getTime()
        : vA < vB ? -1 : vA > vB ? 1 : 0;
      return sortOrder === 'asc' ? cmp : -cmp;
    });

  const paginatedUsers = sortedUsers.slice((currentPage - 1) * perPage, currentPage * perPage);

  // ── Column definitions ───
  const columns = [
    { key: 'id',              label: 'ID',                    minWidth: '60px'  },
    { key: 'reseller_name',   label: `${cfg.singular} Name`,  minWidth: '150px' },
    { key: 'type',            label: 'Type',                  minWidth: '80px'  },
    { key: 'level',           label: 'Level',                 minWidth: '80px'  },
    { key: 'primary_contact', label: 'Primary Contact',       minWidth: '150px' },
    { key: 'reseller_since',  label: `${cfg.singular} Since`, minWidth: '120px' },
    { key: 'status',          label: 'Status',                minWidth: '100px' },
    { key: 'video_conf',      label: 'Video Conf',            minWidth: '110px' },
  ];

  const hlStyle = (user, field) => ({
    backgroundColor: user.id === highlightedUserId && highlightedField === field ? '#FFD700' : '#fff',
    fontFamily,
  });

  // ── Render ─
  return (
    <div style={{ fontFamily, fontSize: `${bodyFontSize}px` }}>
      <div style={{
        backgroundColor: '#fff', padding: '1rem', borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)', fontFamily, marginTop: '10px',
      }}>

        {/* ── HEADER ── */}
        <div style={{
          display: 'flex',
          flexDirection: window.innerWidth < 600 ? 'column' : 'row',
          alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
          justifyContent: 'space-between',
          marginBottom: '1rem', gap: '0.5rem',
        }}>
          <h4 style={{ fontSize: `${titleFontSize}px`, fontWeight: 'bold', marginBottom: 0, color: theme.colors.textPrimary, fontFamily }}>
            {cfg.plural} Management
          </h4>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', justifyContent: window.innerWidth < 600 ? 'stretch' : 'flex-end' }}>

            {/* ── Search ── */}
            <div style={{ position: 'relative', maxWidth: '140px', height: '38px' }}>
              <input
                type="text"
                placeholder={`Search ${cfg.plural.toLowerCase()}...`}
                value={searchTerm}
                onChange={handleSearch}
                onKeyPress={handleKeyPress}
                ref={searchInputRef}
                style={{
                  width: '100%', height: '100%',
                  border: '1px solid #dee2e6', borderRadius: '25px', outline: 'none',
                  padding: '0 40px 0 14px', fontSize: '12px',
                  backgroundColor: '#fff', fontFamily,
                }}
              />
              <button
                onClick={handleSearchSubmit}
                style={{
                  position: 'absolute', right: '2px', top: '2px',
                  width: '34px', height: '34px',
                  background: theme.gradients?.primary || theme.colors.primary,
                  border: 'none', borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}
              >
                <i className="bi bi-search" style={{ color: '#fff', fontSize: '14px' }} />
              </button>
            </div>

            {/* ── Add button — uses routes.addReseller from config ── */}
            <Link
              to={addResellerPath}
              style={{
                background: theme.gradients?.secondary || theme.colors.secondary,
                color: theme.colors.textWhite,
                padding: '6px 12px', borderRadius: '8px',
                fontSize: 'clamp(10px,2.5vw,11px)',
                textDecoration: 'none', minWidth: '120px', textAlign: 'center', fontFamily,
              }}
            >
              Add {cfg.singular}
            </Link>

            {/* ── Hierarchy button — uses routes.nested.resellerHierarchy from config ── */}
            <button
              style={{
                background: theme.gradients?.secondary || theme.colors.secondary,
                color: theme.colors.textWhite,
                padding: '6px 12px', borderRadius: '8px',
                fontSize: 'clamp(10px,2.5vw,11px)',
                border: 'none', minWidth: '120px', cursor: 'pointer', fontFamily,
              }}
              onClick={() => {
                if (!localStorage.getItem('token')) {
                  toast.error('Session expired. Please log in again.');
                  navigate('/login');
                  return;
                }
                navigate(hierarchyPath);
              }}
            >
              User Hierarchy
            </button>
          </div>
        </div>

        <Pagination
          currentPage={currentPage} setCurrentPage={setCurrentPage}
          perPage={perPage}         setPerPage={setPerPage}
          totalItems={sortedUsers.length}
          gotoPage={gotoPage}       setGotoPage={setGotoPage}
        />

        {/* ── TABLE ── */}
        <div style={{
          overflowX: 'auto', backgroundColor: '#fff', borderRadius: '10px',
          border: '1px solid #dee2e6', marginTop: '1rem', marginBottom: '1rem',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: `${bodyFontSize}px`, fontFamily }}>
            <thead>
              <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite }}>
                {columns.map((col) => (
                  <th
                    key={col.key}
                    className="p-2"
                    style={{ cursor: 'pointer', minWidth: col.minWidth, fontFamily }}
                    onClick={() => handleSort(col.key)}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      {col.label}
                      {sortField === col.key && (
                        <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`} />
                      )}
                    </span>
                  </th>
                ))}
                <th className="p-2" style={{ textAlign: 'center', minWidth: '160px', width: '160px', maxWidth: '160px', fontFamily }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((user) => (
                  <tr
                    key={user.id}
                    onMouseEnter={() => setHoveredUserId(user.id)}
                    onMouseLeave={() => setHoveredUserId(null)}
                    style={{ borderBottom: '1px solid #dee2e6' }}
                  >
                    <td className="p-2" style={hlStyle(user, 'id')}>{user.id}</td>
                    <td className="p-2" style={hlStyle(user, 'reseller_name')}>{user.reseller_name}</td>
                    <td className="p-2" style={hlStyle(user, 'type')}>{user.type || 'None'}</td>
                    <td className="p-2" style={hlStyle(user, 'level')}>{user.level || 'None'}</td>
                    <td className="p-2" style={hlStyle(user, 'primary_contact')}>{user.primary_contact}</td>
                    <td className="p-2" style={{ fontFamily }}>{new Date(user.reseller_since).toLocaleDateString()}</td>
                    <td className="p-2" style={{ fontFamily }}>
                      <span style={{ color: user.status === 'Active' ? theme.colors.success : theme.colors.danger, fontWeight: 'bold' }}>
                        {user.status === 'Active' ? 'Active' : 'Deactivated'}
                      </span>
                    </td>
                    <td className="p-2" style={{ fontFamily }}>{user.video_conf === 'enable' ? 'Enabled' : 'Disabled'}</td>
                    <td className="p-2" style={{ textAlign: 'center', minWidth: '160px', width: '160px', maxWidth: '160px', fontFamily }}>
                      <div style={{
                        display: hoveredUserId === user.id ? 'flex' : 'none',
                        justifyContent: 'center', gap: '1rem', alignItems: 'center',
                      }}>
                        <Link
                          to={editResellerPath(user.id)}
                          style={{
                            color: theme.colors.primary,
                            textDecoration: 'underline',
                            fontSize: `${bodyFontSize - 2}px`,
                            fontFamily,
                          }}
                        >
                          Edit
                        </Link>

                        <button
                          onClick={() => handleActivateDeactivate(user.id, user.status)}
                          style={{
                            background: 'none', border: 'none',
                            color: user.status === 'Active' ? theme.colors.danger : theme.colors.success,
                            textDecoration: 'underline', cursor: 'pointer',
                            fontSize: `${bodyFontSize - 2}px`, padding: 0, fontFamily,
                          }}
                        >
                          {user.status === 'Active' ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="text-center p-3" colSpan={9} style={{ fontFamily }}>
                    {searchTerm
                      ? `No ${cfg.singular.toLowerCase()} found with ID, name, type, level, or primary contact "${searchTerm}"`
                      : cfg.emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage} setCurrentPage={setCurrentPage}
          perPage={perPage}         setPerPage={setPerPage}
          totalItems={sortedUsers.length}
          gotoPage={gotoPage}       setGotoPage={setGotoPage}
        />
      </div>

      {/* ── CONFIRM MODAL ── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000,
        }}>
          <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '8px', width: '320px', textAlign: 'center', fontFamily }}>
            <h5 style={{ marginBottom: '10px', color: theme.colors.danger, fontSize: `${titleFontSize}px`, fontFamily }}>
              Confirm Action
            </h5>
            <p style={{ fontSize: `${bodyFontSize}px`, fontFamily }}>
              Are you sure you want to {actionType} this {cfg.singular.toLowerCase()}?
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
              <button
                onClick={() => setShowModal(false)}
                style={{ backgroundColor: theme.colors.secondary, color: theme.colors.textWhite, border: 'none', padding: '8px 16px', borderRadius: '5px', fontFamily, fontSize: `${bodyFontSize}px` }}
              >
                No
              </button>
              <button
                onClick={confirmAction}
                style={{ backgroundColor: theme.colors.danger, color: theme.colors.textWhite, border: 'none', padding: '8px 16px', borderRadius: '5px', fontFamily, fontSize: `${bodyFontSize}px` }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Resellers;